/**
 * Email Tab Component
 * Displays email threads and messages for a contact or deal
 */

import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Mail, Send, Search, RefreshCw, Paperclip, Loader2, ChevronDown, ChevronRight, ChevronLeft, MoreVertical, PenSquare, Archive, Trash2, Reply, ReplyAll, Forward, SlidersHorizontal } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import EmailComposer from "./EmailComposer";
import EmailThreadView from "./EmailThreadView";
import EmailDebugPanel from "./EmailDebugPanel";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

interface EmailTabProps {
  contactId?: string;
  dealId?: string;
  contactEmail?: string;
  contactName?: string;
  /** When set, this sender email is shown with a company icon instead of user avatar (e.g. info@tptbrany.sk) */
  companySendEmail?: string;
}

export default function EmailTab({ contactId, dealId, contactEmail, contactName, companySendEmail }: EmailTabProps) {
  const { t } = useTranslation();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<Array<{ name?: string; email: string }>>([]);
  const [replySubject, setReplySubject] = useState<string | undefined>(undefined);
  const [replyOriginalMessage, setReplyOriginalMessage] = useState<string | undefined>(undefined);
  // CRM `emails.id` of the message being replied to. Passed to the composer so the outgoing
  // message inherits the RFC In-Reply-To / References chain from the parent.
  const [replyParentEmailId, setReplyParentEmailId] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [debugOpen, setDebugOpen] = useState(false);
  const [filterUnreadOnly, setFilterUnreadOnly] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());
  const [replyTrigger, setReplyTrigger] = useState(0);
  const qc = useQueryClient();

  // Build query params (limit for performance)
  const params = new URLSearchParams();
  if (contactId) params.append("contactId", contactId);
  if (dealId) params.append("dealId", dealId);
  if (searchQuery) params.append("q", searchQuery);
  params.set("limit", "50");
  if (filterUnreadOnly) params.set("read", "false");

  // Fetch threads
  const threadsQuery = useQuery({
    queryKey: ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly],
    queryFn: async () => {
      const p = new URLSearchParams(params);
      const res = await fetch(apiUrl(`/api/email/threads?${p.toString()}`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch threads");
      const data = await res.json();
      return data.threads as Array<{
        id: string;
        subject?: string;
        last_message_at: string;
        last_incoming_at?: string;
        last_outgoing_at?: string;
        emails?: Array<{
          id: string;
          direction: "incoming" | "outgoing";
          read_at?: string;
          remote_seen: boolean;
          from_name?: string;
          from_email?: string;
          has_attachments?: boolean;
        }>;
      }>;
    },
    staleTime: 60 * 1000,
  });

  // Mark email as read (with optimistic update)
  const markReadMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const res = await fetch(apiUrl(`/api/email/messages/${emailId}/read`), {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to mark as read");
      return res.json();
    },
    onMutate: async (emailId) => {
      const now = new Date().toISOString();
      const queryKey = ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly];
      const previousThreads = qc.getQueryData(queryKey);
      const previousThread = selectedThreadId
        ? qc.getQueryData(["email-thread", selectedThreadId])
        : undefined;
      if (previousThreads && Array.isArray(previousThreads)) {
        qc.setQueryData(queryKey, (old: typeof previousThreads) =>
          (old || []).map((thread: any) => ({
            ...thread,
            emails: (thread.emails || []).map((e: any) =>
              e.id === emailId ? { ...e, read_at: now, remote_seen: true } : e
            ),
          }))
        );
      }
      if (previousThread && typeof previousThread === "object" && Array.isArray((previousThread as any).messages)) {
        qc.setQueryData(["email-thread", selectedThreadId], {
          ...previousThread,
          messages: (previousThread as any).messages.map((m: any) =>
            m.id === emailId ? { ...m, read_at: now, remote_seen: true } : m
          ),
        });
      }
      return { previousThreads, previousThread };
    },
    onError: (_err, _emailId, context) => {
      const queryKey = ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly];
      if (context?.previousThreads !== undefined) {
        qc.setQueryData(queryKey, context.previousThreads);
      }
      if (context?.previousThread !== undefined && selectedThreadId) {
        qc.setQueryData(["email-thread", selectedThreadId], context.previousThread);
      }
    },
    onSuccess: (_data, emailId) => {
      const threadIdToInvalidate = selectedThreadId;
      const queryKey = ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly];
      const threads = qc.getQueryData(queryKey) as Array<{ id: string; emails?: Array<{ id: string }> }> | undefined;
      const threadIdContainingEmail = threads?.find((t) => t.emails?.some((e) => e.id === emailId))?.id;
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["email-threads"] });
        if (threadIdToInvalidate) {
          qc.invalidateQueries({ queryKey: ["email-thread", threadIdToInvalidate] });
        }
        if (threadIdContainingEmail && threadIdContainingEmail !== threadIdToInvalidate) {
          qc.invalidateQueries({ queryKey: ["email-thread", threadIdContainingEmail] });
        }
      }, 400);
    },
  });

  // Mark email as unread (with optimistic update)
  const markUnreadMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const res = await fetch(apiUrl(`/api/email/messages/${emailId}/unread`), {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to mark as unread");
      return res.json();
    },
    onMutate: async (emailId) => {
      const queryKey = ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly];
      const previousThreads = qc.getQueryData(queryKey);
      const previousThread = selectedThreadId ? qc.getQueryData(["email-thread", selectedThreadId]) : undefined;
      if (previousThreads && Array.isArray(previousThreads)) {
        qc.setQueryData(queryKey, (old: typeof previousThreads) =>
          (old || []).map((thread: any) => ({
            ...thread,
            emails: (thread.emails || []).map((e: any) =>
              e.id === emailId ? { ...e, read_at: null, remote_seen: false } : e
            ),
          }))
        );
      }
      if (previousThread && typeof previousThread === "object" && Array.isArray((previousThread as any).messages)) {
        qc.setQueryData(["email-thread", selectedThreadId], {
          ...previousThread,
          messages: (previousThread as any).messages.map((m: any) =>
            m.id === emailId ? { ...m, read_at: null, remote_seen: false } : m
          ),
        });
      }
      return { previousThreads, previousThread };
    },
    onError: (_err, _emailId, context) => {
      const queryKey = ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly];
      if (context?.previousThreads !== undefined) {
        qc.setQueryData(queryKey, context.previousThreads);
      }
      if (context?.previousThread !== undefined && selectedThreadId) {
        qc.setQueryData(["email-thread", selectedThreadId], context.previousThread);
      }
    },
    onSuccess: (_data, emailId) => {
      const threadIdToInvalidate = selectedThreadId;
      const queryKey = ["email-threads", contactId, dealId, searchQuery, filterUnreadOnly];
      const threads = qc.getQueryData(queryKey) as Array<{ id: string; emails?: Array<{ id: string }> }> | undefined;
      const threadIdContainingEmail = threads?.find((t) => t.emails?.some((e) => e.id === emailId))?.id;
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["email-threads"] });
        if (threadIdToInvalidate) {
          qc.invalidateQueries({ queryKey: ["email-thread", threadIdToInvalidate] });
        }
        if (threadIdContainingEmail && threadIdContainingEmail !== threadIdToInvalidate) {
          qc.invalidateQueries({ queryKey: ["email-thread", threadIdContainingEmail] });
        }
      }, 400);
    },
  });

  const onMarkRead = useCallback((emailId: string) => {
    markReadMutation.mutate(emailId);
  }, []);
  const onMarkUnread = useCallback((emailId: string) => {
    markUnreadMutation.mutate(emailId);
  }, []);

  // Sync emails — dealId/contactId are enough for the server to resolve the real contact email.
  // Only pass contactEmail when known; never invent a fallback (owner/company inbox).
  const syncMutation = useMutation({
    mutationFn: async () => {
      const body: { dealId?: string; contactId?: string; email?: string } = {};
      if (dealId) body.dealId = dealId;
      if (contactId) body.contactId = contactId;
      if (contactEmail?.trim()) body.email = contactEmail.trim();
      
      const res = await fetch(apiUrl("/api/email/sync"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to sync emails");
      return res.json();
    },
    onSuccess: () => {
      const threadId = selectedThreadId;
      // Give the sync backend time to persist new messages, then "hard refresh" only email data
      const refetchDelayMs = 1200;
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["email-threads"] });
        qc.refetchQueries({ queryKey: ["email-threads"], type: "active" });
        if (threadId) {
          qc.invalidateQueries({ queryKey: ["email-thread", threadId] });
          qc.refetchQueries({ queryKey: ["email-thread", threadId], type: "active" });
        }
      }, refetchDelayMs);
    },
  });

  // Keyboard shortcuts: Escape closes thread or clears selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (selectedThreadId) {
        setSelectedThreadId(null);
        e.preventDefault();
      } else if (selectedThreadIds.size > 0) {
        setSelectedThreadIds(new Set());
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedThreadId, selectedThreadIds.size]);

  // Automatically sync when EmailTab is opened with a dealId or contactId
  useEffect(() => {
    if (!dealId && !contactId) return;
    const timer = setTimeout(() => {
      syncMutation.mutate();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, contactId]);

  // Periodic sync so new messages (e.g. sent from desktop) appear without manual refresh (every 2 min)
  useEffect(() => {
    if (!dealId && !contactId) return;
    const interval = setInterval(() => {
      syncMutation.mutate();
    }, 120000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, contactId]);

  const threads = threadsQuery.data || [];
  // Only incoming messages count as "unread" for badge/filter; outgoing are not shown as unread
  const hasUnread = threads.some((thread) =>
    thread.emails?.some((e) => e.direction === "incoming" && !e.read_at && !e.remote_seen)
  );
  const totalMessageCount = threads.reduce((acc, t) => acc + (t.emails?.length ?? 0), 0);

  const isMobile = useIsMobile();

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full overflow-x-hidden max-w-full">
        {/* Single row: left = inbox bar, right = icon bar + search. Ideiglenesen mobilnézetben elrejtve. */}
        {!isMobile && (
        <div className={cn("flex border-b min-h-[3rem]", isMobile && "flex-wrap")}>
          {/* Left column: same width as thread list on desktop; full width on mobile */}
          <div className={cn("border-r flex items-center px-3 py-2 gap-2 flex-wrap min-w-0", isMobile ? "flex-1 min-w-0" : "w-1/3")}>
            <Mail className="h-5 w-5 text-primary shrink-0" aria-hidden />
            <h3 className="font-semibold truncate text-sm hidden sm:block">{t("email.title")}</h3>
            {hasUnread && <Badge className="shrink-0 bg-primary/20 text-primary border-0">{t("email.new")}</Badge>}
            <span className="text-sm text-muted-foreground">{t("email.totalMessages", { count: totalMessageCount })}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8 shrink-0", filterUnreadOnly ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary")}
                  onClick={() => setFilterUnreadOnly((v) => !v)}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.onlyUnread")}</TooltipContent>
            </Tooltip>
            {threads.length > 0 && !isMobile && (
              <div className="flex items-center gap-2 text-sm">
                <button type="button" onClick={() => setSelectedThreadIds(new Set(threads.map((t) => t.id)))} className="text-primary hover:underline">
                  {t("email.selectAll")}
                </button>
                <span className="text-muted-foreground">|</span>
                <button type="button" onClick={() => setSelectedThreadIds(new Set())} className="text-muted-foreground hover:underline disabled:opacity-50" disabled={selectedThreadIds.size === 0}>
                  {t("email.deselectAll")}
                </button>
              </div>
            )}
            {threads.length > 0 && isMobile && (
              <div className="flex-1" />
            )}
          </div>
          {/* Right column: action icons + search only (on mobile same row, flex-1) */}
          <div className={cn("flex items-center gap-0.5 p-2 flex-wrap min-w-0", isMobile ? "flex-1" : "flex-1", isMobile && "gap-0.5")}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-primary hover:bg-primary/10 shrink-0 [&_svg]:size-5"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending || threadsQuery.isFetching}
                >
                  {syncMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.downloadNew")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-primary hover:bg-primary/10 shrink-0 [&_svg]:size-5" onClick={() => setComposerOpen(true)}>
                  <PenSquare className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.newMessage")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" disabled className="h-10 w-10 text-primary/70 opacity-60 cursor-not-allowed shrink-0 [&_svg]:size-5">
                  <Archive className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.comingSoon")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" disabled className="h-10 w-10 text-primary/70 opacity-60 cursor-not-allowed shrink-0 [&_svg]:size-5">
                  <Trash2 className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.comingSoon")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-primary hover:bg-primary/10 shrink-0 [&_svg]:size-5"
                  disabled={!selectedThreadId}
                  onClick={() => selectedThreadId && setReplyTrigger((t) => t + 1)}
                >
                  <Reply className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.reply")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" disabled className="h-10 w-10 text-primary/70 opacity-60 cursor-not-allowed shrink-0 [&_svg]:size-5">
                  <ReplyAll className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.comingSoon")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" disabled className="h-10 w-10 text-primary/70 opacity-60 cursor-not-allowed shrink-0 [&_svg]:size-5">
                  <Forward className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("email.comingSoon")}</TooltipContent>
            </Tooltip>
            <div className="flex-1 min-w-0" aria-hidden />
            <div className="w-36 sm:w-44 shrink-0">
              <Input
                placeholder={t("email.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 text-sm"
              />
            </div>
          </div>
        </div>
        )}
        {selectedThreadIds.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 border-b">
            <Button variant="outline" size="sm" className="text-primary border-primary/50 hover:bg-primary/10"
              onClick={() => {
                selectedThreadIds.forEach((threadId) => {
                  const thread = threads.find((t) => t.id === threadId);
                  thread?.emails?.filter((e) => !e.read_at && !e.remote_seen).forEach((e) => markReadMutation.mutate(e.id));
                });
              }}
            >
              {t("email.markSelectedRead")}
            </Button>
            <Button variant="outline" size="sm" className="text-primary border-primary/50 hover:bg-primary/10"
              onClick={() => {
                selectedThreadIds.forEach((threadId) => {
                  const thread = threads.find((t) => t.id === threadId);
                  const lastIncoming = thread?.emails?.filter((e) => e.direction === "incoming").pop();
                  const emailId = lastIncoming?.id ?? thread?.emails?.[thread.emails.length - 1]?.id;
                  if (emailId) markUnreadMutation.mutate(emailId);
                });
              }}
            >
              {t("email.markSelectedUnread")}
            </Button>
          </div>
        )}

      {/* Main content: on mobile show either list OR thread (with back); on desktop show list + thread side by side */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Mobile: when thread selected, show only thread view with back button */}
        {isMobile && selectedThreadId ? (
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden w-full">
            <div className="flex items-center gap-2 shrink-0 border-b px-2 py-2 bg-background">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setSelectedThreadId(null)}
                aria-label={t("email.backToList")}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="truncate text-sm font-medium flex-1 min-w-0">
                {threads.find((t) => t.id === selectedThreadId)?.subject || t("email.noSubject")}
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <EmailThreadView
                threadId={selectedThreadId}
                onMarkRead={onMarkRead}
                onMarkUnread={onMarkUnread}
                companySendEmail={companySendEmail}
                onReply={(toEmails, subject, originalMessage, parentEmailId) => {
                  setReplyToEmail(toEmails);
                  setReplySubject(subject);
                  setReplyOriginalMessage(originalMessage);
                  setReplyParentEmailId(parentEmailId);
                  setComposerOpen(true);
                }}
                dealId={dealId}
                replyTrigger={replyTrigger}
                onReplyTriggered={() => setReplyTrigger(0)}
              />
            </div>
          </div>
        ) : (
          <>
            {/* Thread list — full width on mobile, 1/3 on desktop */}
            <div className={cn("flex flex-col min-w-0 min-h-0", isMobile ? "w-full" : "w-1/3 border-r")}>
              <ScrollArea className="flex-1 min-h-0">
                {threadsQuery.isLoading ? (
                  <div className="p-4 text-center text-muted-foreground">{t("email.loading")}</div>
                ) : threads.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">{t("email.noEmail")}</div>
                ) : (
                  <div className="divide-y">
                    {threads.map((thread) => {
                      const unreadCount = thread.emails?.filter((e) => e.direction === "incoming" && !e.read_at && !e.remote_seen).length || 0;
                      const latestEmail = thread.emails?.[thread.emails.length - 1];
                      const senderLabel = latestEmail?.from_name || latestEmail?.from_email || "";
                      const isSelected = selectedThreadIds.has(thread.id);
                      const lastIncomingEmail = thread.emails?.filter((e) => e.direction === "incoming").pop();
                      const hasAttachment = latestEmail?.has_attachments;

                      return (
                        <div
                          key={thread.id}
                          className={cn(
                            "flex items-stretch border-l-4 transition-colors",
                            (unreadCount > 0 || isSelected) ? "border-l-primary" : "border-l-transparent",
                            isSelected && "bg-primary/10 dark:bg-primary/20",
                            !isSelected && selectedThreadId === thread.id && !isMobile && "bg-muted"
                          )}
                        >
                          {!isMobile && (
                            <div
                              className="flex items-center pl-2 shrink-0"
                              onClick={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => {
                                  setSelectedThreadIds((prev) => {
                                    const next = new Set(prev);
                                    if (checked) next.add(thread.id);
                                    else next.delete(thread.id);
                                    return next;
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={thread.subject || "Szál"}
                              />
                            </div>
                          )}
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedThreadId(thread.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedThreadId(thread.id);
                              }
                            }}
                            className={cn(
                              "flex-1 min-w-0 text-left hover:bg-muted/50 transition-colors cursor-pointer",
                              isMobile ? "p-3" : "p-3 pr-1",
                              !isSelected && selectedThreadId === thread.id && !isMobile && "bg-muted"
                            )}
                          >
                            {isMobile ? (
                              /* Mobile: compact row — direction pill, sender, subject, date (+ optional attachment) */
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <Badge
                                      variant={latestEmail?.direction === "incoming" ? "secondary" : "default"}
                                      className="shrink-0 h-4 px-1.5 text-[10px] font-medium"
                                    >
                                      {latestEmail?.direction === "incoming" ? `← ${t("email.incoming")}` : `→ ${t("email.outgoing")}`}
                                    </Badge>
                                    {senderLabel && (
                                      <span className="text-sm font-medium truncate text-foreground min-w-0">{senderLabel}</span>
                                    )}
                                  </div>
                                  <div className={cn("text-sm truncate mt-0.5", unreadCount > 0 && "text-primary font-medium")}>
                                    {thread.subject || `(${t("email.noSubject")})`}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {new Date(thread.last_message_at).toLocaleDateString(undefined, {
                                      year: "numeric",
                                      month: "numeric",
                                      day: "numeric",
                                    })}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {hasAttachment && <Paperclip className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
                                  {unreadCount > 0 && (
                                    <Badge className="bg-primary/20 text-primary border-0 text-xs">{unreadCount}</Badge>
                                  )}
                                </div>
                              </div>
                            ) : (
                              /* Desktop: subject-first row with direction pill next to sender + timestamp on the right. */
                              <>
                                <div className="flex items-start justify-between gap-1 mb-1">
                                  <div className="flex-1 min-w-0">
                                    <div className={cn("font-medium truncate", unreadCount > 0 && "text-primary")}>
                                      {thread.subject || `(${t("email.noSubject")})`}
                                    </div>
                                    {latestEmail && (
                                      <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                        <Badge
                                          variant={latestEmail.direction === "incoming" ? "secondary" : "default"}
                                          className="shrink-0 h-4 px-1.5 text-[10px] font-medium"
                                        >
                                          {latestEmail.direction === "incoming" ? `← ${t("email.incoming")}` : `→ ${t("email.outgoing")}`}
                                        </Badge>
                                        {senderLabel && (
                                          <span className="text-sm text-muted-foreground truncate min-w-0">{senderLabel}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {hasAttachment && (
                                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
                                    )}
                                    {unreadCount > 0 && (
                                      <Badge className="ml-1 bg-primary/20 text-primary border-0">
                                        {unreadCount}
                                      </Badge>
                                    )}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 p-0 shrink-0">
                                          <MoreVertical className="h-4 w-4" />
                                          <span className="sr-only">{t("email.menu")}</span>
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenuItem
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const emailId = lastIncomingEmail?.id ?? thread.emails?.[thread.emails.length - 1]?.id;
                                            if (emailId) markUnreadMutation.mutate(emailId);
                                          }}
                                        >
                                          <Mail className="h-4 w-4 mr-2" />
                                          {t("email.markUnread")}
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(thread.last_message_at).toLocaleDateString("hu-HU", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
              {!isMobile && (
                <Collapsible open={debugOpen} onOpenChange={setDebugOpen} className="border-t">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-start gap-2 rounded-none">
                      {debugOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {t("email.diagnostika")}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="max-h-[40vh] overflow-y-auto p-2">
                      <EmailDebugPanel
                        contactId={contactId}
                        dealId={dealId}
                        contactEmail={contactEmail}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>

            {/* Thread view — only on desktop when thread selected; on mobile handled above */}
            {!isMobile && (
              <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
                {selectedThreadId ? (
                  <EmailThreadView
                    threadId={selectedThreadId}
                    onMarkRead={onMarkRead}
                    onMarkUnread={onMarkUnread}
                    companySendEmail={companySendEmail}
                    onReply={(toEmails, subject, originalMessage, parentEmailId) => {
                      setReplyToEmail(toEmails);
                      setReplySubject(subject);
                      setReplyOriginalMessage(originalMessage);
                      setReplyParentEmailId(parentEmailId);
                      setComposerOpen(true);
                    }}
                    dealId={dealId}
                    replyTrigger={replyTrigger}
                    onReplyTriggered={() => setReplyTrigger(0)}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    {t("email.selectThread")}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Email composer */}
      {composerOpen && (
        <EmailComposer
          contactId={contactId}
          dealId={dealId}
          to={replyToEmail.length > 0 ? replyToEmail : (contactEmail ? [{ email: contactEmail, name: contactName }] : [])}
          replySubject={replySubject}
          replyOriginalMessage={replyOriginalMessage}
          // Reply flow: parent message id first, fall back to the currently open thread so
          // "new message from this thread" still attaches to the correct email_threads row.
          parentEmailId={replyParentEmailId}
          threadId={!replyParentEmailId && selectedThreadId ? selectedThreadId : undefined}
          onClose={() => {
            setComposerOpen(false);
            setReplyToEmail([]);
            setReplySubject(undefined);
            setReplyOriginalMessage(undefined);
            setReplyParentEmailId(undefined);
          }}
          onSent={() => {
            setComposerOpen(false);
            setReplyToEmail([]);
            setReplySubject(undefined);
            setReplyOriginalMessage(undefined);
            setReplyParentEmailId(undefined);
            qc.invalidateQueries({ queryKey: ["email-threads"] });
            if (selectedThreadId) {
              qc.invalidateQueries({ queryKey: ["email-thread", selectedThreadId] });
            }
            // Trigger sync so new messages (incl. sent from desktop) appear soon without manual refresh
            syncMutation.mutate();
          }}
        />
      )}
      </div>
    </TooltipProvider>
  );
}

